import React, { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import api from "../utils/api";
import { DASHBOARD_BLOCKED_MESSAGE } from "../utils/dashboardAccess";
import "./Chatbot.css";

const storageKeys = {
  token: "chatbot_token",
  user: "chatbot_user",
  username: "chatbot_username",
};

const loadSession = () => {
  try {
    // Check chatbot-specific session first
    let token = localStorage.getItem(storageKeys.token);
    let userRaw = localStorage.getItem(storageKeys.user);

    // Fallback to main dashboard session if chatbot session is not found
    if (!token || !userRaw) {
      token = localStorage.getItem("token");
      userRaw = localStorage.getItem("user");
    }

    const user = userRaw ? JSON.parse(userRaw) : null;
    if (!token || !user) {
      return null;
    }
    return { token, user };
  } catch {
    return null;
  }
};

const formatTime = (value) =>
  new Date(value).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

function parseInlineMarkdown(text) {
  if (!text) return "";
  const parts = [];
  const boldRegex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = boldRegex.exec(text)) !== null) {
    const precedingText = text.substring(lastIndex, match.index);
    if (precedingText) {
      parts.push(precedingText);
    }
    parts.push(<strong key={match.index}>{match[1]}</strong>);
    lastIndex = boldRegex.lastIndex;
  }

  const remainingText = text.substring(lastIndex);
  if (remainingText) {
    parts.push(remainingText);
  }

  return parts.length > 0 ? parts : text;
}

function renderMarkdown(text) {
  if (!text) return null;

  const lines = text.split("\n");
  const elements = [];

  let currentList = [];
  let currentListType = null; // "bullet" or "ordered"
  let currentTable = null; // { headers: [], rows: [] }
  let currentParagraph = [];

  const flushParagraph = (key) => {
    if (currentParagraph.length > 0) {
      elements.push(
        <p key={`p-${key}`}>
          {currentParagraph.map((line, idx) => (
            <React.Fragment key={idx}>
              {idx > 0 && <br />}
              {parseInlineMarkdown(line)}
            </React.Fragment>
          ))}
        </p>
      );
      currentParagraph = [];
    }
  };

  const flushList = (key) => {
    if (currentList.length > 0) {
      const Tag = currentListType === "ordered" ? "ol" : "ul";
      elements.push(
        <Tag key={`list-${key}`}>
          {currentList.map((item, idx) => (
            <li key={idx}>{parseInlineMarkdown(item)}</li>
          ))}
        </Tag>
      );
      currentList = [];
      currentListType = null;
    }
  };

  const flushTable = (key) => {
    if (currentTable) {
      elements.push(
        <div className="chatbot-table-wrapper" key={`table-${key}`}>
          <table className="chatbot-table">
            <thead>
              <tr>
                {currentTable.headers.map((col, idx) => (
                  <th key={idx}>{parseInlineMarkdown(col)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {currentTable.rows.map((row, rIdx) => (
                <tr key={rIdx}>
                  {row.map((cell, cIdx) => (
                    <td key={cIdx}>{parseInlineMarkdown(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      currentTable = null;
    }
  };

  const flushAll = (key) => {
    flushParagraph(key);
    flushList(key);
    flushTable(key);
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // 1. Heading line
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      flushAll(i);
      const level = headingMatch[1].length;
      const Tag = `h${level}`;
      elements.push(<Tag key={`h-${i}`}>{parseInlineMarkdown(headingMatch[2])}</Tag>);
      continue;
    }

    // 2. Table line
    if (line.startsWith("|")) {
      flushParagraph(i);
      flushList(i);

      const cells = line.split("|").slice(1, -1).map(c => c.trim());

      if (!currentTable) {
        // First line of the table (headers)
        currentTable = { headers: cells, rows: [] };
      } else {
        // Is it the delimiter row? e.g. |:---|:---|
        const isDelimiter = cells.every(c => c.replace(/[:-\s]/g, "") === "");
        if (!isDelimiter) {
          currentTable.rows.push(cells);
        }
      }
      continue;
    }

    // 3. Bullet list line
    if (line.startsWith("- ") || line.startsWith("* ")) {
      flushParagraph(i);
      flushTable(i);
      if (currentListType !== "bullet") {
        flushList(i);
        currentListType = "bullet";
      }
      currentList.push(line.substring(2));
      continue;
    }

    // 4. Ordered list line
    const orderedMatch = line.match(/^(\d+)\.\s+(.+)$/);
    if (orderedMatch) {
      flushParagraph(i);
      flushTable(i);
      if (currentListType !== "ordered") {
        flushList(i);
        currentListType = "ordered";
      }
      currentList.push(orderedMatch[2]);
      continue;
    }

    // 5. Empty line (separates blocks)
    if (!line) {
      flushAll(i);
      continue;
    }

    // 6. Regular text line (accumulates into paragraph)
    flushList(i);
    flushTable(i);
    currentParagraph.push(line);
  }

  // Flush any remaining blocks
  flushAll(lines.length);

  return elements;
}

function Chatbot() {
  const [session, setSession] = useState(() => loadSession());
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [chatEntries, setChatEntries] = useState([]);
  const [sendError, setSendError] = useState("");
  const [sendLoading, setSendLoading] = useState(false);

  const streamEndRef = useRef(null);

  useEffect(() => {
    document.documentElement.classList.add("app-html-chatbot");
    document.body.classList.add("app-body-chatbot");
    return () => {
      document.documentElement.classList.remove("app-html-chatbot");
      document.body.classList.remove("app-body-chatbot");
    };
  }, []);

  const clientName = useMemo(
    () => session?.user?.client_name || session?.user?.username || "Enviz client",
    [session],
  );

  const username = useMemo(() => {
    return localStorage.getItem("username") || localStorage.getItem(storageKeys.username) || session?.user?.username || "demo";
  }, [session]);

  const scrollToBottom = () => {
    streamEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatEntries]);

  const handleLoginChange = (event) => {
    const { name, value } = event.target;
    setLoginForm((current) => ({ ...current, [name]: value }));
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError("");
    setLoginLoading(true);

    try {
      const response = await api.post("/signin", {
        username: loginForm.username.trim(),
        password: loginForm.password,
      });
      const user = {
        ...response.data.client,
        token: response.data.token,
      };

      if (user.effective_dashboard_access === false) {
        setLoginError(DASHBOARD_BLOCKED_MESSAGE);
        return;
      }

      localStorage.setItem(storageKeys.token, response.data.token);
      localStorage.setItem(storageKeys.user, JSON.stringify(user));
      localStorage.setItem(storageKeys.username, response.data.client.username || loginForm.username.trim());

      setSession({ token: response.data.token, user });
      setChatEntries([]);
      setMessage("");
    } catch (error) {
      if (error?.response?.status === 403) {
        setLoginError(error?.response?.data?.detail || DASHBOARD_BLOCKED_MESSAGE);
      } else {
        setLoginError("Invalid username or password.");
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem(storageKeys.token);
    localStorage.removeItem(storageKeys.user);
    localStorage.removeItem(storageKeys.username);
    // Also clear main session if we want to log out completely
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("username");
    setSession(null);
    setChatEntries([]);
    setMessage("");
  };

  const handleSend = async (event) => {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }
    const text = message.trim();

    if (!text || !session?.token || sendLoading) {
      return;
    }

    setSendError("");
    setSendLoading(true);

    const pendingEntry = {
      id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
      text,
      status: "saving",
      timestamp: new Date().toISOString(),
    };

    // Append to end of stream
    setChatEntries((current) => [...current, pendingEntry]);

    try {
      // 1. Save user query to DB (postgres)
      await api.post(
        "/chatbot/message",
        { query: text },
        {
          headers: {
            Authorization: `Bearer ${session.token}`,
          },
        },
      );

      // Update user message state as saved in DB (✓ Sent)
      setChatEntries((current) =>
        current.map((entry) =>
          entry.id === pendingEntry.id
            ? {
              ...entry,
              status: "saved",
            }
            : entry,
        ),
      );
      setMessage("");

      // 2. Add pending bot entry to the end of stream
      const botPendingId = `${Date.now()}-bot`;
      const botPendingEntry = {
        id: botPendingId,
        text: "Thinking...",
        status: "thinking",
        isBot: true,
        timestamp: new Date().toISOString(),
      };
      setChatEntries((current) => [...current, botPendingEntry]);

      // 3. Call local Ensight AI agent
      try {
        const aiResponse = await axios.post("https://enfoenviz.com/api/chat", {
          username: username,
          question: text,
        });

        setChatEntries((current) =>
          current.map((entry) =>
            entry.id === botPendingId
              ? {
                ...entry,
                text: aiResponse.data.response,
                status: "done",
              }
              : entry,
          ),
        );
      } catch (aiError) {
        console.error("AI chatbot error:", aiError);
        setChatEntries((current) =>
          current.map((entry) =>
            entry.id === botPendingId
              ? {
                ...entry,
                text: "Sorry, I could not reach the Ensight AI agent. Please make sure the backend is running at https://enfoenviz.com/api",
                status: "error",
              }
              : entry,
          ),
        );
      }

    } catch (error) {
      setSendError("Could not send the message. Please try again.");
      setChatEntries((current) => current.filter((entry) => entry.id !== pendingEntry.id));
    } finally {
      setSendLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  if (!session) {
    return (
      <div className="chatbot-page">
        <header className="chatbot-topbar">
          <div className="chatbot-brand">
            <div className="chatbot-logo-wrapper">
              <h2 className="chatbot-brand-title">
                <span className="chatbot-brand-primary">En</span>
                <span className="chatbot-brand-secondary">Viz</span>
              </h2>
            </div>
            <div>
              <div className="chatbot-title">enViz Chatbot</div>
              <div className="chatbot-subtitle">Sign in to access Ensight AI</div>
            </div>
          </div>
          <span className="chatbot-badge">AI Assistant</span>
        </header>

        <main className="chatbot-login-layout">
          <section className="chatbot-hero">
            <p className="chatbot-eyebrow">AI Assistant</p>
            <h1>Optimize your energy consumption with Ensight.</h1>
            <p>
              Log in using your enViz credentials to check machine runtimes, discover load shifting savings, and analyze power metrics.
            </p>
          </section>

          <section className="chatbot-card chatbot-login-card">
            <div className="chatbot-card-header">
              <div>
                <h2>Login</h2>
                <p>Enter your credentials to begin.</p>
              </div>
            </div>

            <form className="chatbot-form" onSubmit={handleLogin}>
              <label>
                Username
                <input
                  name="username"
                  value={loginForm.username}
                  onChange={handleLoginChange}
                  placeholder="Username"
                  autoComplete="username"
                />
              </label>

              <label>
                Password
                <input
                  name="password"
                  type="password"
                  value={loginForm.password}
                  onChange={handleLoginChange}
                  placeholder="Password"
                  autoComplete="current-password"
                />
              </label>

              {loginError && <div className="chatbot-error">{loginError}</div>}

              <button type="submit" className="chatbot-primary" disabled={loginLoading}>
                {loginLoading ? "Signing in..." : "Sign in"}
              </button>
            </form>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="chatbot-page chatbot-page-signed-in">
      <header className="chatbot-topbar">
        <div className="chatbot-brand">
          <div className="chatbot-logo-wrapper">
            <h2 className="chatbot-brand-title">
              <span className="chatbot-brand-primary">En</span>
              <span className="chatbot-brand-secondary">Viz</span>
            </h2>
          </div>
        </div>
        <div className="chatbot-session">
          <div className="chatbot-session-copy">
            <strong>{clientName}</strong>
          </div>
          <button type="button" className="chatbot-ghost" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <main className="chatbot-main">
        <section className="chatbot-panel">
          <div className="chatbot-stream" aria-live="polite">
            {chatEntries.length === 0 ? (
              <div className="chatbot-empty">
                <h3>Start by typing a message below.</h3>
                <p>Ask anything about machine runtimes, load shifting, or saving recommendations.</p>
              </div>
            ) : (
              chatEntries.map((entry) => (
                <article key={entry.id} className={`chatbot-message-row ${entry.isBot ? "bot" : "user"}`}>
                  <div className={`chatbot-message ${entry.isBot ? "bot" : "user"}`}>
                    <div>
                      {entry.isBot ? (
                        entry.status === "thinking" ? (
                          <div className="chatbot-typing-indicator" aria-label="Thinking">
                            <span className="dot"></span>
                            <span className="dot"></span>
                            <span className="dot"></span>
                          </div>
                        ) : (
                          renderMarkdown(entry.text)
                        )
                      ) : (
                        <p>{entry.text}</p>
                      )}
                    </div>
                    <div className="chatbot-message-footer">
                      <small>{formatTime(entry.timestamp)}</small>
                    </div>
                  </div>
                </article>
              ))
            )}
            <div ref={streamEndRef} />
          </div>

          {sendError && <div className="chatbot-error chatbot-send-error">{sendError}</div>}

          <form className="chatbot-composer" onSubmit={handleSend}>
            <div className="chatbot-composer-input-row">
              <textarea
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask Ensight a question..."
                rows={1}
              />
              <button type="submit" className="chatbot-primary" disabled={sendLoading || !message.trim()}>
                {sendLoading ? "Sending..." : "Send"}
              </button>
            </div>
          </form>
        </section>
      </main>
    </div>
  );
}

export default Chatbot;